import { parseFramework } from "@/lib/svgUtils";
import { jsPDF } from "jspdf";
import "svg2pdf.js";

// Build a guide-free, flattened composite SVG string from a framework + placed graphics + part colors.
// graphics items must include a `dataUrl` (base64) for their image.
export function buildCompositeSvgString(frameworkSvg, graphics, partColors) {
  const { viewBox, inner, partShapes } = parseFramework(frameworkSvg);
  const vb = (viewBox || "0 0 800 600").split(/[\s,]+/).map(Number);
  const vx = vb[0] || 0, vy = vb[1] || 0, vw = vb[2] || 800, vh = vb[3] || 600;
  const clips = Object.entries(partShapes || {})
    .map(([id, shape]) => `<clipPath id="clip-${id}" clipPathUnits="userSpaceOnUse">${shape}</clipPath>`)
    .join("");
  const colorRects = Object.entries(partColors || {})
    .filter(([, c]) => c)
    .map(([id, c]) => `<rect x="${vx}" y="${vy}" width="${vw}" height="${vh}" fill="${c}" clip-path="url(#clip-${id})"/>`)
    .join("");
  const imgs = (graphics || [])
    .map((g) => {
      if (!g.dataUrl) return "";
      return `<g clip-path="url(#clip-${g.partId})" opacity="${g.opacity ?? 1}"><image href="${g.dataUrl}" x="${g.cx - g.w / 2}" y="${g.cy - g.h / 2}" width="${g.w}" height="${g.h}" transform="rotate(${g.rotation || 0} ${g.cx} ${g.cy})" preserveAspectRatio="none"/></g>`;
    })
    .join("");
  const str = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" width="${vw}" height="${vh}"><defs>${clips}</defs>${inner}${colorRects}${imgs}</svg>`;
  return { str, vw, vh };
}

// Load an SVG string as a fully decoded <img> (blob URL avoids huge encoded data URLs).
async function loadSvgImage(svgStr) {
  const url = URL.createObjectURL(new Blob([svgStr], { type: "image/svg+xml" }));
  const img = new Image();
  try {
    await new Promise((res, rej) => { img.onload = res; img.onerror = () => rej(new Error("SVG render failed")); img.src = url; });
    if (img.decode) await img.decode().catch(() => {});
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
  return img;
}

const canvasToBlob = (canvas, type, q) => new Promise((res, rej) =>
  canvas.toBlob((b) => (b ? res(b) : rej(new Error("Canvas too large to export"))), type, q));

// Fetch every artwork file referenced by the design (with retries) and attach it as a
// base64 dataUrl. Throws if ANY referenced file can't be loaded so packages are never partial.
export async function loadGraphicsData(graphics, fetchObjectUrl, attempts = 3) {
  const ids = [...new Set((graphics || []).map((g) => g.fileId).filter(Boolean))];
  const map = {};
  const missing = [];
  const queue = [...ids];
  const worker = async () => {
    while (queue.length) {
      const id = queue.shift();
      for (let i = 0; i < attempts; i++) {
        try {
          const dataUrl = await blobUrlToDataUrl(await fetchObjectUrl(id));
          if (!dataUrl || dataUrl.length < 64) throw new Error("empty file");
          map[id] = dataUrl;
          break;
        } catch (e) {
          if (i === attempts - 1) missing.push(id);
          else await new Promise((r) => setTimeout(r, 600 * (i + 1)));
        }
      }
    }
  };
  await Promise.all([worker(), worker()]);
  const noFile = (graphics || []).filter((g) => !g.fileId).length;
  if (missing.length || noFile) {
    throw new Error(`${missing.length + noFile} customer graphic${missing.length + noFile > 1 ? "s" : ""} could not be loaded`);
  }
  return (graphics || []).map((g) => ({ ...g, dataUrl: map[g.fileId] }));
}

// Rasterize an SVG string to a PNG Blob at the given intrinsic size * scale.
export async function rasterizeSvgToPng(svgStr, vw, vh, scale = 4) {
  const img = await loadSvgImage(svgStr);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(vw * scale);
  canvas.height = Math.round(vh * scale);
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return await canvasToBlob(canvas, "image/png");
}

// Fetch a blob URL and convert to a base64 data URL.
export async function blobUrlToDataUrl(url) {
  const res = await fetch(url);
  const blob = await res.blob();
  URL.revokeObjectURL(url);
  return await new Promise((r, j) => { const fr = new FileReader(); fr.onload = () => r(fr.result); fr.onerror = () => j(fr.error); fr.readAsDataURL(blob); });
}

// Rasterize an SVG string to an offscreen canvas at the given intrinsic size * scale.
async function rasterizeSvgToCanvas(svgStr, vw, vh, scale, background, rotate = false) {
  const img = await loadSvgImage(svgStr);
  const w = Math.round(vw * scale);
  const h = Math.round(vh * scale);
  const canvas = document.createElement("canvas");
  canvas.width = rotate ? h : w;   // swap dims for 90° rotation
  canvas.height = rotate ? w : h;
  const ctx = canvas.getContext("2d");
  if (background) { ctx.fillStyle = background; ctx.fillRect(0, 0, canvas.width, canvas.height); }
  if (rotate) {
    ctx.translate(canvas.width, 0); // 90° clockwise: move origin to top-right then rotate
    ctx.rotate(Math.PI / 2);
  }
  ctx.drawImage(img, 0, 0, w, h);
  return canvas;
}

// Render at full requested scale, only bounded by the browser's hard canvas limit so wide
// designs never silently fail (Chrome caps a canvas side at 16384px).
const MAX_RASTER_DIM = 16000;
const effectiveScale = (vw, vh, scale) => Math.max(1, Math.min(scale, MAX_RASTER_DIM / Math.max(vw, vh)));

// Build a vector-based PDF, rotated 90° clockwise (page dimensions swapped): framework
// lines/text/solid fills stay vector (via svg2pdf), customer raster art is embedded as a
// single transparent overlay image (avoids svg2pdf's image-data recursion on large base64).
export async function svgStringToPdfBlob(svgStr, vw, vh) {
  const NS = "http://www.w3.org/2000/svg";
  const root = new DOMParser().parseFromString(svgStr, "image/svg+xml").documentElement;
  const pw = vh, ph = vw;                  // rotated page: width/height swapped
  const rot = `translate(${vh},0) rotate(90)`;

  // Vector layer: everything except the raster <image> elements, wrapped in a 90° CW group.
  const vectorEl = root.cloneNode(true);
  vectorEl.querySelectorAll("image").forEach((im) => im.remove());
  const g = vectorEl.ownerDocument.createElementNS(NS, "g");
  g.setAttribute("transform", rot);
  while (vectorEl.firstChild) g.appendChild(vectorEl.firstChild);
  vectorEl.appendChild(g);
  vectorEl.setAttribute("viewBox", `0 0 ${pw} ${ph}`);
  vectorEl.setAttribute("width", pw);
  vectorEl.setAttribute("height", ph);

  const doc = new jsPDF({ orientation: pw >= ph ? "landscape" : "portrait", unit: "pt", format: [pw, ph] });
  const holder = document.createElement("div");
  holder.style.cssText = "position:absolute;left:-99999px;top:0;width:0;height:0;overflow:hidden";
  holder.appendChild(vectorEl);
  document.body.appendChild(holder);
  try {
    await doc.svg(vectorEl, { x: 0, y: 0, width: pw, height: ph });
  } finally {
    document.body.removeChild(holder);
  }

  // Raster art layer: keep <defs> (clips) + groups holding <image>, drop framework visuals,
  // rasterize (rotated 90° CW, transparent) and embed on top of the vector layer.
  if (root.querySelector("image")) {
    const artClone = root.cloneNode(true);
    [...artClone.children].forEach((child) => {
      const tag = (child.tagName || "").toLowerCase();
      if (tag === "defs") return;
      if (child.querySelector && child.querySelector("image")) return;
      child.remove();
    });
    artClone.setAttribute("width", vw);
    artClone.setAttribute("height", vh);
    const artStr = new XMLSerializer().serializeToString(artClone);
    const canvas = await rasterizeSvgToCanvas(artStr, vw, vh, effectiveScale(vw, vh, 4), null, true);
    // "FAST" = lossless Flate compression (no image artifacts) at a compact size.
    doc.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, pw, ph, undefined, "FAST");
  }

  return doc.output("blob");
}

// Generate the two production print formats (vector PDF + JPG), both rotated 90° clockwise.
export async function buildPrintFormats(svgStr, vw, vh, scale = 4) {
  const s = effectiveScale(vw, vh, scale);
  const canvas = await rasterizeSvgToCanvas(svgStr, vw, vh, s, "#ffffff", true);
  const jpg = await canvasToBlob(canvas, "image/jpeg", 0.95);
  const pdf = await svgStringToPdfBlob(svgStr, vw, vh);
  return { pdf, jpg, width: canvas.width, height: canvas.height };
}

// Convert a Blob to a base64 data URL.
export async function blobToDataUrl(blob) {
  return await new Promise((r) => { const fr = new FileReader(); fr.onload = () => r(fr.result); fr.readAsDataURL(blob); });
}

function ellipsize(ctx, text, maxW) {
  const t = String(text || "");
  if (ctx.measureText(t).width <= maxW) return t;
  let s = t;
  while (s.length > 1 && ctx.measureText(s + "…").width > maxW) s = s.slice(0, -1);
  return s + "…";
}

// Build a print-ready "summary sheet" collation page for a bulk print package.
// items: [{ imgDataUrl, customerName, orderNumber, productName, itemLabel }]
// - Each thumbnail is rendered at a fixed 2in display width (aspect ratio preserved),
//   with the customer name + order details directly underneath.
// - Entries flow left-to-right in columns inside a page no wider than 11in.
// - The ENTIRE sheet is horizontally mirrored on output for the Roland Texart XT-640 /
//   Ergosoft RIP dye-sublimation transfer workflow (prints correct after transfer).
// Returns { mirroredPdf, mirroredPng, readablePng } blobs.
export async function buildSummarySheet(items, { title = "Bulk Print Summary", dpi = 300, pageWidthIn = 11 } = {}) {
  const DPI = dpi;
  const pageW = Math.round(pageWidthIn * DPI);        // hard width cap (11in)
  const margin = Math.round(0.3 * DPI);
  const gutter = Math.round(0.3 * DPI);
  const thumbW = Math.round(2 * DPI);                 // fixed 2in thumbnail width
  const cellW = thumbW;
  const cols = Math.max(1, Math.floor((pageW - 2 * margin + gutter) / (cellW + gutter)));
  const textLH = Math.round(0.16 * DPI);
  const textH = textLH * 3 + Math.round(0.08 * DPI);
  const rowGap = Math.round(0.35 * DPI);
  const headerH = Math.round(0.95 * DPI);

  // Preload thumbnails to read their natural aspect ratios.
  const imgs = await Promise.all((items || []).map((it) => new Promise((res) => {
    if (!it.imgDataUrl) return res({ im: null, w: 1, h: 1 });
    const im = new Image();
    im.onload = () => res({ im, w: im.naturalWidth || 1, h: im.naturalHeight || 1 });
    im.onerror = () => res({ im: null, w: 1, h: 1 });
    im.src = it.imgDataUrl;
  })));
  const thumbHs = imgs.map(({ w, h }) => Math.max(1, Math.round(thumbW * (h / w))));

  const rows = Math.ceil(items.length / cols);
  let y = headerH;
  const rowY = [];
  for (let r = 0; r < rows; r++) {
    let maxThumb = 0;
    for (let c = 0; c < cols; c++) {
      const idx = r * cols + c;
      if (idx < items.length) maxThumb = Math.max(maxThumb, thumbHs[idx]);
    }
    rowY.push(y);
    y += maxThumb + Math.round(0.1 * DPI) + textH + rowGap;
  }
  const pageH = Math.min(MAX_RASTER_DIM, y + margin);

  const base = document.createElement("canvas");
  base.width = pageW; base.height = pageH;
  const ctx = base.getContext("2d");
  ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, pageW, pageH);
  ctx.textBaseline = "top"; ctx.textAlign = "left";

  // Header
  ctx.fillStyle = "#111111";
  ctx.font = `bold ${Math.round(0.26 * DPI)}px Arial, sans-serif`;
  ctx.fillText(title, margin, margin);
  ctx.fillStyle = "#666666";
  ctx.font = `${Math.round(0.13 * DPI)}px Arial, sans-serif`;
  ctx.fillText(`${items.length} order${items.length !== 1 ? "s" : ""} · ${new Date().toLocaleString()} · MIRRORED FOR RIP`, margin, margin + Math.round(0.32 * DPI));

  const gridW = cols * cellW + (cols - 1) * gutter;
  const startX = Math.round((pageW - gridW) / 2);

  items.forEach((it, idx) => {
    const r = Math.floor(idx / cols), c = idx % cols;
    const x = startX + c * (cellW + gutter);
    const cy = rowY[r];
    const th = thumbHs[idx];
    const { im } = imgs[idx];
    ctx.fillStyle = "#f3f1ea"; ctx.fillRect(x, cy, thumbW, th);
    if (im) ctx.drawImage(im, x, cy, thumbW, th);
    ctx.strokeStyle = "#cfc9b8"; ctx.lineWidth = Math.max(1, Math.round(0.006 * DPI)); ctx.strokeRect(x, cy, thumbW, th);

    let ty = cy + th + Math.round(0.08 * DPI);
    ctx.fillStyle = "#111111"; ctx.font = `bold ${Math.round(0.125 * DPI)}px Arial, sans-serif`;
    ctx.fillText(ellipsize(ctx, it.customerName || "—", thumbW), x, ty); ty += textLH;
    ctx.fillStyle = "#333333"; ctx.font = `${Math.round(0.11 * DPI)}px Arial, sans-serif`;
    ctx.fillText(ellipsize(ctx, [it.orderNumber, it.itemLabel].filter(Boolean).join(" · "), thumbW), x, ty); ty += textLH;
    ctx.fillStyle = "#777777"; ctx.font = `${Math.round(0.1 * DPI)}px Arial, sans-serif`;
    ctx.fillText(ellipsize(ctx, it.productName || "", thumbW), x, ty);
  });

  // Mirror the whole sheet horizontally.
  const mirror = document.createElement("canvas");
  mirror.width = pageW; mirror.height = pageH;
  const mctx = mirror.getContext("2d");
  mctx.translate(pageW, 0); mctx.scale(-1, 1);
  mctx.drawImage(base, 0, 0);

  const mirroredPng = await new Promise((r) => mirror.toBlob(r, "image/png"));
  const readablePng = await new Promise((r) => base.toBlob(r, "image/png"));

  // Wrap the mirrored raster into an exactly-11in-wide PDF (72pt/in).
  const ptW = pageWidthIn * 72;
  const ptH = (pageH / pageW) * ptW;
  const doc = new jsPDF({ orientation: ptW >= ptH ? "landscape" : "portrait", unit: "pt", format: [ptW, ptH] });
  doc.addImage(mirror.toDataURL("image/png"), "PNG", 0, 0, ptW, ptH, undefined, "FAST");
  const mirroredPdf = doc.output("blob");

  return { mirroredPdf, mirroredPng, readablePng };
}
